import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { format, isValid, parseISO } from 'date-fns';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import {
  applicationStatusLabel,
  walletTransactionStatusLabel,
  walletTransactionTypeLabel,
} from '../admin-payout-labels/admin-payout-labels';
import {
  AdminBriefApplicationCleanupItem,
  AdminBriefApplicationCleanupLookup,
  AdminBriefApplicationCleanupStatusUser,
} from './brief-application-cleanup.types';
import { AdminBriefApplicationCleanupService } from '../../services/admin-brief-application-cleanup.service';
import { AdminSettingsContextService } from '../../services/admin-settings-context.service';
import { autoLoadOnContextKey } from '../../services/admin-settings-auto-load';

@Component({
  selector: 'admin-tool-brief-application-cleanup',
  imports: [DatePipe],
  templateUrl: './brief-application-cleanup.html',
  styleUrl: './brief-application-cleanup.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefApplicationCleanup {
  readonly settingsContext = inject(AdminSettingsContextService);

  searchLoading = signal(false);
  deleteLoadingId = signal<string | null>(null);
  lookupResult = signal<AdminBriefApplicationCleanupLookup | null>(null);
  searchError = signal<string | null>(null);

  readonly applicationStatusLabel = applicationStatusLabel;
  readonly walletTransactionStatusLabel = walletTransactionStatusLabel;
  readonly walletTransactionTypeLabel = walletTransactionTypeLabel;

  hasDuplicates = computed(() => !!this.lookupResult()?.hasDuplicates);

  private destroyRef = inject(DestroyRef);
  private cleanupService = inject(AdminBriefApplicationCleanupService);
  private alertService = inject(AlertService);
  private toastNotificationService = inject(ToastNotificationService);

  constructor() {
    autoLoadOnContextKey(() => {
      const briefId = this.settingsContext.briefId().trim();
      const creatorId = this.settingsContext.creatorId().trim();
      return briefId && creatorId ? `${briefId}|${creatorId}` : null;
    }, () => this.runSearch());
  }

  runSearch(): void {
    const briefId = this.settingsContext.briefId().trim();
    const creatorId = this.settingsContext.creatorId().trim();

    if (!briefId || !creatorId) {
      void this.toastNotificationService.alert({
        title: 'Missing fields',
        message: 'Enter both brief ID and creator ID.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.searchLoading.set(true);
    this.searchError.set(null);
    this.lookupResult.set(null);

    this.cleanupService
      .lookup(briefId, creatorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.searchLoading.set(false);
          this.lookupResult.set(result);
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.searchLoading.set(false);
          const message = this.extractErrorMessage(err);
          this.searchError.set(message);
          void this.toastNotificationService.alert({
            title: 'Lookup failed',
            message,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  rowClass(app: AdminBriefApplicationCleanupItem): string {
    const base =
      'rounded-xl border p-4 transition-colors';
    if (app.isLowerProgressDuplicate) {
      return `${base} border-amber-400/70 bg-amber-50/80 ring-1 ring-amber-300/60 dark:border-amber-600/50 dark:bg-amber-950/30 dark:ring-amber-700/40`;
    }
    if (app.isDuplicate) {
      return `${base} border-emerald-400/50 bg-emerald-50/50 dark:border-emerald-700/40 dark:bg-emerald-950/20`;
    }
    return `${base} border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950`;
  }

  async deleteApplication(app: AdminBriefApplicationCleanupItem): Promise<void> {
    if (!app.canDelete) {
      return;
    }

    const lookup = this.lookupResult();
    const rehireTarget = lookup?.rehireTarget;
    if (!rehireTarget) {
      void this.toastNotificationService.alert({
        title: 'Cannot prepare re-hire',
        message: 'No valid application was found to reset after removing the duplicate.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const creatorId = this.settingsContext.creatorId().trim();
    const invalidStatusLabel = applicationStatusLabel(app.applicationStatus);
    const validApp = lookup.applications.find((item) => item.id === rehireTarget.validApplicationId);
    const validStatusLabel = applicationStatusLabel(rehireTarget.validApplicationStatus);
    const walletNote = rehireTarget.walletTransactionId
      ? `Remove wallet transaction ${rehireTarget.walletTransactionId} on the valid application (funds returned to brand wallet).`
      : 'No wallet transaction found on the valid application.';

    const confirmed = await this.alertService.confirm({
      title: 'Remove duplicate and prepare re-hire',
      message:
        `This will run the following steps:\n\n` +
        `1. Permanently delete ${app.statusHistory.length} status history row(s) for the invalid application ${app.id} (${invalidStatusLabel}).\n` +
        `2. Permanently delete the invalid application ${app.id} (falls back to soft delete only if hard delete is blocked).\n` +
        `3. On the valid application ${rehireTarget.validApplicationId} (${validStatusLabel}):\n` +
        `   a) ${walletNote}\n` +
        `   b) Reset application status to Applied (2)\n` +
        `   c) Add a new Applied status history record\n\n` +
        `After this you can re-hire the creator from manage-brief using application ${rehireTarget.validApplicationId}.` +
        (validApp?.statusHistory.length
          ? `\n\nThe valid application currently has ${validApp.statusHistory.length} status history row(s); those existing rows are kept.`
          : ''),
      confirmText: 'Confirm cleanup',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    this.deleteLoadingId.set(app.id);

    this.cleanupService
      .deleteApplication(app.id, creatorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.deleteLoadingId.set(null);
          void this.toastNotificationService.alert({
            title: 'Duplicate removed — ready to re-hire',
            message:
              `Invalid application ${result.invalidApplicationId}: removed ${result.deletedInvalidStatusCount} status row(s)` +
              `${result.invalidApplicationHardDeleted ? ' and hard-deleted application' : ' and soft-deleted application'}.` +
              ` Valid application ${result.validApplicationId} reset to Applied` +
              `${result.validWalletTransactionDeleted ? ' with wallet reservation released' : ''}` +
              `${result.validStatusHistoryAdded ? ' and new Applied history added' : ''}.`,
            type: 'success',
            icon: 'success',
          });
          this.runSearch();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.deleteLoadingId.set(null);
          void this.toastNotificationService.alert({
            title: 'Delete failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  formatMoney(amount: number, currencyCode: string): string {
    const major = amount / 100;
    return `${currencyCode} ${major.toFixed(2)}`;
  }

  formatStatusDate(value: string | Date): string {
    const date = value instanceof Date ? value : parseISO(value);
    if (!isValid(date)) {
      return String(value);
    }
    return format(date, 'dd/MM/yyyy h:mma').toLowerCase();
  }

  statusUserLabel(user: AdminBriefApplicationCleanupStatusUser | null): string {
    if (!user) {
      return 'Unknown user';
    }
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (name && user.email) {
      return `${name} (${user.email})`;
    }
    return name || user.email || 'Unknown user';
  }

  private extractErrorMessage(err: {
    error?: { message?: string | string[] };
    message?: string;
  }): string {
    const nested = err?.error?.message;
    if (Array.isArray(nested)) {
      return nested.join(', ');
    }
    if (typeof nested === 'string' && nested.length) {
      return nested;
    }
    return err?.message ?? 'Request failed';
  }
}
